import type { AppendMessage, ThreadMessageLike } from "@assistant-ui/react";
import { useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { useSalonId } from "@/lib/store/salon";
import type { StreamEvent } from "../api/ai-agent-client";
import { aiAgentClient } from "../api/ai-agent-client";
import { useClientToolsStore } from "../store/client-tools-store";
import { useViewContextStore } from "../store/view-context-store";
import { useAiChatStore } from "../stores/ai-chat.store";

/**
 * Hook to manage AI chat streaming lifecycle
 *
 * This hook manages all asynchronous API calls and side effects.
 * It is stateless and uses the Zustand store for state management.
 */
export function useAiChatStreaming() {
  const salonId = useSalonId();
  const { t } = useTranslation("errors");

  // Get state and actions from Zustand store
  const {
    messages,
    threadId,
    handleStreamEvent,
    addUserMessage,
    addErrorMessage,
    setIsRunning,
    setThreadId,
    setMessages,
  } = useAiChatStore();

  // Refs to track active stream cleanup functions for cancellation
  const streamCleanupRef = useRef<(() => void) | null>(null);
  const resumeCleanupRef = useRef<(() => void) | null>(null);

  /**
   * Handle error events from stream
   */
  const handleErrorEvent = useCallback(
    (event: StreamEvent) => {
      if (event.type !== "error") return;

      // Get localized error message if code is provided
      const localizedMessage = event.data.code
        ? t(`graphql.${event.data.code}`, { defaultValue: event.data.message })
        : event.data.message;

      addErrorMessage(localizedMessage);
      setIsRunning(false);
    },
    [t, addErrorMessage, setIsRunning]
  );

  /**
   * Handle done events and execute pending tools if needed
   */
  const handleDoneEvent = useCallback(
    async (
      event: StreamEvent,
      pendingToolExecution: { toolName: string; args: Record<string, any> } | null
    ) => {
      if (event.type !== "done") return;

      const newThreadId = event.data.threadId;
      setThreadId(newThreadId);

      // If there's a pending tool, execute it and resume
      if (pendingToolExecution) {
        const { toolName, args } = pendingToolExecution;

        // Get tool implementation from store
        const toolImpl = useClientToolsStore.getState().getToolImplementation(toolName);
        const toolReferences = useClientToolsStore.getState().getToolReferences();

        if (!toolImpl) {
          console.error("[handleDoneEvent] Tool not found:", toolName);
          setIsRunning(false);
          return;
        }
        try {
          // Execute tool
          const result = await toolImpl.execute(args);

          // Track pending tool execution for nested resumes
          let nestedPendingTool: { toolName: string; args: Record<string, any> } | null = null;
          // Resume with result
          const resumeCleanup = aiAgentClient.resume(
            salonId,
            newThreadId,
            { type: "toolResult", toolName, result },
            toolReferences,
            (resumeEvent) => {
              // Handle client tool execution (nested interrupt)
              if (resumeEvent.type === "client_tool_execution") {
                nestedPendingTool = {
                  toolName: resumeEvent.data.toolName,
                  args: resumeEvent.data.args,
                };
                return;
              }

              if (resumeEvent.type === "done") {
                // Check if there's a nested pending tool
                if (nestedPendingTool) {
                  // Recursive: execute nested tool and resume again
                  handleDoneEvent(resumeEvent, nestedPendingTool);
                } else {
                  setIsRunning(false);
                }
                return;
              }

              if (resumeEvent.type === "error") {
                handleErrorEvent(resumeEvent);
                return;
              }

              // Handle other resume events
              handleStreamEvent(resumeEvent);
            }
          );

          // Store resume cleanup function in ref for cancellation
          resumeCleanupRef.current = resumeCleanup;
        } catch (error) {
          // Track pending tool execution for nested resumes (error path)
          let nestedPendingTool: { toolName: string; args: Record<string, any> } | null = null;

          // Resume with error - just return error property directly
          aiAgentClient.resume(
            salonId,
            newThreadId,
            {
              type: "toolResult",
              toolName,
              result: {
                error: error instanceof Error ? error.message : "Unknown error",
              },
            },
            toolReferences,
            (resumeEvent) => {
              // Handle client tool execution (nested interrupt)
              if (resumeEvent.type === "client_tool_execution") {
                nestedPendingTool = {
                  toolName: resumeEvent.data.toolName,
                  args: resumeEvent.data.args,
                };
                return;
              }

              if (resumeEvent.type === "done") {
                // Check if there's a nested pending tool
                if (nestedPendingTool) {
                  // Recursive: execute nested tool and resume again
                  handleDoneEvent(resumeEvent, nestedPendingTool);
                } else {
                  setIsRunning(false);
                }
                return;
              }
              if (resumeEvent.type === "error") {
                handleErrorEvent(resumeEvent);
                return;
              }
              handleStreamEvent(resumeEvent);
            }
          );
        }
      } else {
        // Fetch suggestions asynchronously (non-blocking)
        aiAgentClient
          .fetchSuggestions(salonId, newThreadId)
          .then((suggestions) => {
            if (suggestions.length > 0) {
              useAiChatStore.getState().setSuggestions(suggestions);
            }
          })
          .catch((error) => {
            console.warn("[handleDoneEvent] Failed to fetch suggestions:", error);
          });

        // Stop running immediately (don't wait for suggestions)
        setIsRunning(false);
      }
    },
    [salonId, setThreadId, setIsRunning, handleStreamEvent, handleErrorEvent]
  );

  /**
   * Stream a new message to the AI agent
   */
  const streamNewMessage = useCallback(
    async ({ message, attachmentIds }: { message: AppendMessage; attachmentIds?: string[] }) => {
      // Extract text from user message
      const textContent = message.content.find((c) => c.type === "text");
      if (!textContent || textContent.type !== "text") {
        throw new Error("Only text messages are supported");
      }

      // Add user message to UI (including attachments)
      const userMessage: ThreadMessageLike = {
        id: `user-${Date.now()}`,
        role: "user",
        content: message.content,
        attachments: message.attachments,
        createdAt: new Date(),
      };
      addUserMessage(userMessage);

      // Start streaming
      setIsRunning(true);

      // Store client tool execution info within this stream scope
      let pendingToolExecution: { toolName: string; args: Record<string, any> } | null = null;

      try {
        // Get view context and tools from store
        const viewContext = useViewContextStore.getState().getAllShortContexts();
        const toolReferences = useClientToolsStore.getState().getToolReferences();

        // Store cleanup function for cancellation
        const cleanup = aiAgentClient.streamMessage(
          salonId,
          {
            message: textContent.text,
            threadId,
            clientTools: toolReferences,
            attachmentIds: attachmentIds && attachmentIds.length > 0 ? attachmentIds : undefined,
            viewContext: viewContext || undefined,
          },
          async (event) => {
            // Store client tool execution request
            if (event.type === "client_tool_execution") {
              pendingToolExecution = {
                toolName: event.data.toolName,
                args: event.data.args,
              };
              return;
            }

            // Handle error
            if (event.type === "error") {
              handleErrorEvent(event);
              return;
            }

            // Handle done - execute pending tool if any, then resume
            if (event.type === "done") {
              await handleDoneEvent(event, pendingToolExecution);
              return;
            }

            // Handle all other events (text, tool_call, tool_result, interrupt, suggestions)
            handleStreamEvent(event);
          }
        );

        // Store cleanup function in ref for cancellation
        streamCleanupRef.current = cleanup;
      } catch (error) {
        addErrorMessage(error instanceof Error ? error.message : "An unexpected error occurred");
        setIsRunning(false);
        throw error;
      }
    },
    [
      salonId,
      threadId,
      addUserMessage,
      setIsRunning,
      handleStreamEvent,
      handleErrorEvent,
      handleDoneEvent,
      addErrorMessage,
    ]
  );

  /**
   * Reload/regenerate the last assistant message
   */
  const streamReload = useCallback(
    async (parentId: string | null) => {
      // Find the parent message index
      const parentIndex = parentId ? messages.findIndex((m) => m.id === parentId) : -1;

      // Remove all messages after the parent (or all if no parent)
      const messagesToKeep = parentIndex >= 0 ? messages.slice(0, parentIndex + 1) : [];
      setMessages(messagesToKeep);

      // Get the last user message to resend
      const lastUserMessage = [...messagesToKeep].reverse().find((m) => m.role === "user");

      if (!lastUserMessage) {
        return;
      }

      // Extract text from the user message
      const content = lastUserMessage.content;
      if (typeof content === "string") {
        return;
      }

      const textContent = content.find((c) => c.type === "text");
      if (!textContent || textContent.type !== "text") {
        return;
      }

      // Start streaming
      setIsRunning(true);

      // Store client tool execution info within this stream scope
      let pendingToolExecution: { toolName: string; args: Record<string, any> } | null = null;

      try {
        // Get tools from store
        const toolReferences = useClientToolsStore.getState().getToolReferences();

        aiAgentClient.streamMessage(
          salonId,
          {
            message: textContent.text,
            threadId: threadId || undefined,
            clientTools: toolReferences,
          },
          async (event) => {
            // Store client tool execution request
            if (event.type === "client_tool_execution") {
              pendingToolExecution = {
                toolName: event.data.toolName,
                args: event.data.args,
              };
              return;
            }

            // Handle error
            if (event.type === "error") {
              handleErrorEvent(event);
              return;
            }

            // Handle done - execute pending tool if any, then resume
            if (event.type === "done") {
              await handleDoneEvent(event, pendingToolExecution);
              return;
            }

            // Handle all other events (text, tool_call, tool_result, interrupt)
            handleStreamEvent(event);
          }
        );
      } catch (error) {
        addErrorMessage(error instanceof Error ? error.message : "An unexpected error occurred");
        setIsRunning(false);
      }
    },
    [
      salonId,
      threadId,
      messages,
      setMessages,
      setIsRunning,
      handleStreamEvent,
      handleErrorEvent,
      handleDoneEvent,
      addErrorMessage,
    ]
  );

  /**
   * Load an existing thread from history by streaming events
   */
  const streamHistory = useCallback(
    async (threadIdToLoad: string) => {
      // Reset state
      setThreadId(threadIdToLoad);
      setMessages([]);
      setIsRunning(true);

      // Stream history events using the same event handling as live streaming
      aiAgentClient.streamThreadHistory(salonId, threadIdToLoad, (event) => {
        // Handle done event
        if (event.type === "done") {
          setIsRunning(false);
          return;
        }

        // Handle error events
        if (event.type === "error") {
          setIsRunning(false);
          return;
        }

        // Process all other events using the store's event handler
        handleStreamEvent(event);
      });
    },
    [salonId, setThreadId, setMessages, setIsRunning, handleStreamEvent]
  );

  /**
   * Cancel active stream
   */
  const cancelStream = useCallback(async () => {
    // Close active stream connection
    if (streamCleanupRef.current) {
      streamCleanupRef.current();
      streamCleanupRef.current = null;
    }

    // Close resume stream connection
    if (resumeCleanupRef.current) {
      resumeCleanupRef.current();
      resumeCleanupRef.current = null;
    }

    setIsRunning(false);
  }, [setIsRunning]);

  return {
    streamNewMessage,
    streamReload,
    streamHistory,
    cancelStream,
  };
}
